/**
 * @deprecated This file is deprecated. Use the canonical versions from '../utils/api.js' instead.
 * - tokenUtils: import { tokenUtils } from '../utils/api'
 * - buildUrl: use the buildUrl from '../utils/api'
 * - fetchWithRetry: use fetchWithTimeout from '../utils/api'
 *
 * This file's tokenUtils ignores the keepSignedIn parameter and always uses localStorage,
 * which breaks sessionStorage-based sessions. Do not add new imports from this file.
 */
import config from '../utils/config';

/**
 * @deprecated Use buildUrl from '../utils/api.js' instead.
 * Build API URL with optional function key
 * @param {string} service - Service name (logreg, managebusiness, etc.)
 * @param {string} endpoint - Endpoint path (e.g., '/login')
 * @returns {string} - Full URL
 */
export const buildUrl = (service, endpoint) => {
  // In development, use localhost proxy for all services to avoid CORS
  if (import.meta.env.DEV) {
    const url = `/api${endpoint}`;

    if (config.USE_FUNCTION_KEYS) {
      // Use master host key if available (works for all functions)
      if (config.MASTER_HOST_KEY) {
        return `${url}?code=${config.MASTER_HOST_KEY}`;
      }

      // Use individual function key
      const functionName = endpoint.replace('/', '');
      const functionKey = config.FUNCTION_KEYS[functionName];
      if (functionKey) {
        return `${url}?code=${functionKey}`;
      }
    }

    return url;
  }

  // For production, use direct URLs
  const baseUrl = config.API_URLS[service];

  if (!baseUrl) {
    throw new Error(`Unknown service: ${service}`);
  }

  const url = `${baseUrl}${endpoint}`;

  if (config.USE_FUNCTION_KEYS) {
    // Use master host key if available (works for all functions)
    if (config.MASTER_HOST_KEY) {
      return `${url}?code=${config.MASTER_HOST_KEY}`;
    }

    // Use individual function key
    const functionName = endpoint.replace('/', '');
    const functionKey = config.FUNCTION_KEYS[functionName];
    if (functionKey) {
      return `${url}?code=${functionKey}`;
    }
  }

  return url;
};

/**
 * @deprecated Use tokenUtils from '../utils/api.js' instead.
 * This version ignores keepSignedIn and always uses localStorage.
 */
export const tokenUtils = {
  // Store tokens in localStorage
  storeTokens(token, refreshKey) {
    localStorage.setItem('sessionToken', token);
    localStorage.setItem('refreshKey', refreshKey);
  },

  // Get stored tokens
  getTokens() {
    return {
      token: localStorage.getItem('sessionToken'),
      refreshKey: localStorage.getItem('refreshKey')
    };
  },

  // Clear stored tokens
  clearTokens() {
    localStorage.removeItem('sessionToken');
    localStorage.removeItem('refreshKey');
  },

  // Check if user is authenticated
  isAuthenticated() {
    const { token } = this.getTokens();
    return !!token;
  },

  // Decode token to get user info (simple base64 decode - not for security validation!)
  decodeToken(token) {
    try {
      const payload = token.split('.')[1];
      const decoded = JSON.parse(atob(payload));
      return {
        success: true,
        data: {
          userId: decoded.userId || decoded.sub,
          employeeId: decoded.employeeId,
          role: decoded.role,
          businessId: decoded.businessId || decoded.tennantId || decoded.tenantId,
          tokenType: decoded.tokenType,
          iat: decoded.iat,
          exp: decoded.exp
        }
      };
    } catch (error) {
      return {
        success: false,
        error: 'Invalid token format'
      };
    }
  }
};

/**
 * @deprecated Use fetchWithTimeout from '../utils/api.js' instead.
 * Enhanced fetch with retry logic for Azure Function cold starts
 * @param {string} url - The URL to fetch
 * @param {object} options - Fetch options
 * @param {number} maxRetries - Maximum number of retries (default: 2)
 * @param {number} retryDelay - Delay between retries in ms (default: 2000)
 * @returns {Promise<Response>}
 */
export const fetchWithRetry = async (url, options = {}, maxRetries = 2, retryDelay = 2000) => {
  let lastError;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      // Add timeout to detect cold starts
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

      const response = await fetch(url, {
        ...options,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      // If successful response, return it
      if (response.ok) {
        return response;
      }

      // If server error (500-599) or timeout, retry
      if (response.status >= 500 || response.status === 503) {
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, retryDelay));
          continue;
        }
      }

      // For other errors (4xx), don't retry
      return response;

    } catch (error) {
      lastError = error;

      // If it's an abort/timeout or network error, retry
      if (error.name === 'AbortError' || error.message.includes('fetch')) {
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, retryDelay));
          continue;
        }
      }

      // For other errors, throw immediately
      throw error;
    }
  }

  // If all retries failed, throw the last error
  throw lastError || new Error('All retry attempts failed');
};
